import { useEffect, useState } from "react";
import {
  Box,
  Flex,
  useDisclosure,
  Table,
  TableContainer,
  Tbody,
  Td,
  Tr,
  Tooltip,
  Image,
} from "@chakra-ui/react";
import { ButtonCustom } from "../../../form/controls/form.button";
import { CardCustom } from "../../../shared/CardCustom";
import { CardTitle } from "../../../shared/CardTitle";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store";
import AddIcon from "@mui/icons-material/Add";
import { Role } from "../../../../models/enum/role.enum";
import { AddIntegrationModal } from "./add-integration.modal";
import { integrationService } from "../../../../service/integrations/integration.service";
import { EditIntegrationModal } from "./edit-integration.modal";
import { ArrowRightIcon } from "lucide-react";

export const AccountIntegrationCard = ({}) => {
  const { role, email } = useSelector((state: RootState) => state.userProfile);

  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const fetchIntegrations = async () => {
    setLoading(true);
    const integrations = await integrationService.getIntegrations();
    setIntegrations(integrations);
    setLoading(false);
  };

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const columns = [
    {
      header: "Target",
      callback: (_, integration) => {
        return (
          <Flex flexDir="row" alignItems="center" w="300px" gap="12px">
            <Box position="relative" w="42px" h="42px">
              <Image
                src={`/assets/icons/${integration.target.toLowerCase()}.svg`}
                alt={integration.target}
                w="42px"
                h="42px"
              />
              <Box
                position="absolute"
                bottom="-2px"
                right="-4px"
                w="14px"
                h="14px"
                bg="green.400"
                border="2px solid white"
                borderRadius="full"
              />
            </Box>
            <Flex flexDir="column" gap="2px">
              <Box fontWeight="600">{integration.target}</Box>
              <Box fontSize="sm" color="gray.500">
                {integration.apiUrl}
              </Box>
            </Flex>
          </Flex>
        );
      },
    },
    {
      accessor: "id",
      callback: (_, integration) => {
        return (
          <Tooltip label="Manage" placement="bottom">
            <Box
              onClick={() => {
                setSelectedIntegration(integration);
              }}
              border="1px solid"
              borderColor="gray.200"
              borderRadius="4px"
              p="4px"
              color="gray"
              cursor="pointer"
              _hover={{ color: "brand.brown.100" }}
            >
              <ArrowRightIcon strokeWidth={1.5} />
            </Box>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <>
      <CardCustom
        gap="6px"
        px={{ base: "16px", md: "40px" }}
        py={{ base: "16px", md: "24px" }}
      >
        <Flex justifyContent="space-between" alignItems="center">
          <CardTitle fontSize="20px">Integrations</CardTitle>
          <ButtonCustom
            hidden={!!integrations?.length}
            onClick={onOpen}
            neutral
            style={{
              width: "160px",
              height: "30px",
              backgroundColor: "transparent",
            }}
          >
            <AddIcon />
            Add integration
          </ButtonCustom>
        </Flex>
        {!integrations?.length && !loading && (
          <Flex
            border="1px dashed"
            borderColor="gray.200"
            borderRadius="8px"
            p="24px"
            justifyContent="center"
            alignItems="center"
            mt="24px"
            flexDir="column"
          >
            <Image
              src="/assets/images/integration-placeholder.jpg"
              alt="No integrations"
              w="130px"
              h="130px"
            />
            <Box textAlign="center" fontSize="14px">
              No integrations... yet.
            </Box>
          </Flex>
        )}
        {!!integrations?.length && (
          <TableContainer w="100%">
            <Table
              variant="simple"
              mt="24px"
              display={{ base: "none", md: "block" }}
            >
              <Tbody>
                {integrations.map((row, i) => (
                  <Tr key={i}>
                    {columns.map((column, j) => (
                      <Td
                        key={j}
                        py={1}
                        mr="36px"
                        w="100%"
                        pl={j === 0 && 0}
                        borderBottom="none"
                      >
                        {column.callback ? (
                          <>{column.callback(row[column.accessor], row)}</>
                        ) : (
                          <>{row[column.accessor]}</>
                        )}
                      </Td>
                    ))}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </CardCustom>

      <AddIntegrationModal
        isOpen={isOpen}
        onClose={onClose}
        onSubmit={() => {
          fetchIntegrations();
          onClose();
        }}
      />
      <EditIntegrationModal
        integration={selectedIntegration}
        isOpen={!!selectedIntegration}
        onClose={() => {
          setSelectedIntegration(null);
        }}
        onSubmit={() => {
          fetchIntegrations();
          setSelectedIntegration(null);
        }}
      />
    </>
  );
};
